import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useMutation, useLazyQuery } from "@apollo/client/react";
import {
  Loader2,
  BookOpen,
  Save,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import { GET_THEORY_PROMPT_LAYERS, GET_EXERCISES_BY_SKILL } from "@/graphql/queries/admin";
import {
  GENERATE_THEORY,
  SAVE_THEORY_SKILL_PROMPT,
  SAVE_THEORY_PROMPT_LAYER,
} from "@/graphql/mutations/admin";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TheoryView } from "@/components/theory-view";

interface LayerInfo {
  key: string;
  label: string;
  filename: string;
  path: string;
  exists: boolean;
  content: string | null;
}

interface ComposedSegment {
  type: "layer" | "shape_pack" | "coverage" | "concepts" | "skill";
  label: string;
  content: string;
}

interface LayersData {
  layers: LayerInfo[];
  composed_segments: ComposedSegment[];
}

export default function TheoryGeneratePage() {
  const [searchParams] = useSearchParams();

  const skillKey = searchParams.get("skill") || "";
  const skillName = searchParams.get("name") || skillKey;
  const subject = searchParams.get("subject") || "math";
  const grade = parseInt(searchParams.get("grade") || "1");

  // Settings
  const [model, setModel] = useState("default");

  // Layers
  const [layersData, setLayersData] = useState<LayersData | null>(null);
  const [layersLoading, setLayersLoading] = useState(false);
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());
  const [editedLayers, setEditedLayers] = useState<Record<string, string>>({});
  const [showComposed, setShowComposed] = useState(false);

  // Existing theory
  const [theoryHtml, setTheoryHtml] = useState<string | null>(null);
  const [theoryLoading, setTheoryLoading] = useState(false);

  // Generation
  const [generating, setGenerating] = useState(false);

  const [fetchLayers] = useLazyQuery(GET_THEORY_PROMPT_LAYERS, { fetchPolicy: "network-only" });
  const [fetchTheory] = useLazyQuery(GET_EXERCISES_BY_SKILL, { fetchPolicy: "network-only" });
  const [saveSkillPrompt, { loading: savingSkill }] = useMutation(SAVE_THEORY_SKILL_PROMPT);
  const [savePromptLayer, { loading: savingLayer }] = useMutation(SAVE_THEORY_PROMPT_LAYER);
  const [generateTheory] = useMutation(GENERATE_THEORY);

  const loadLayers = useCallback(async () => {
    if (!skillKey || !subject) return;
    setLayersLoading(true);
    try {
      const { data } = await fetchLayers({
        variables: { subject, grade, skillKey },
      });
      const result = data?.theoryPromptLayers
        ? typeof data.theoryPromptLayers === "string"
          ? JSON.parse(data.theoryPromptLayers)
          : data.theoryPromptLayers
        : null;
      setLayersData(result);
      setEditedLayers({});
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      toast.error("Nepavyko užkrauti prompt sluoksnių");
    } finally {
      setLayersLoading(false);
    }
  }, [skillKey, subject, grade, fetchLayers]);

  const loadTheory = useCallback(async () => {
    if (!skillKey || !subject) return;
    setTheoryLoading(true);
    try {
      const { data } = await fetchTheory({
        variables: { subject, grade, skill: skillKey, level: 1 },
      });
      setTheoryHtml(data?.exercisesBySkill?.theory || null);
    } catch {
      setTheoryHtml(null);
    } finally {
      setTheoryLoading(false);
    }
  }, [skillKey, subject, grade, fetchTheory]);

  useEffect(() => {
    loadLayers();
    loadTheory();
  }, [loadLayers, loadTheory]);

  const toggleLayer = (key: string) => {
    setExpandedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setLayerContent = (key: string, content: string) => {
    setEditedLayers((prev) => ({ ...prev, [key]: content }));
  };

  const isLayerModified = (layer: LayerInfo) => {
    return layer.key in editedLayers && editedLayers[layer.key] !== (layer.content || "");
  };

  const handleSaveLayer = async (layer: LayerInfo) => {
    const content = editedLayers[layer.key] ?? layer.content ?? "";

    if (layer.key === "skill") {
      try {
        const { data } = await saveSkillPrompt({
          variables: { skillKey, subject, grade, content },
        });
        if (data?.saveTheorySkillPrompt?.success) {
          toast.success("Išsaugota");
          await loadLayers();
        } else {
          toast.error(data?.saveTheorySkillPrompt?.error || "Klaida");
        }
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : "Klaida");
      }
    } else {
      try {
        const { data } = await savePromptLayer({
          variables: { layerKey: layer.key, content, subject, grade },
        });
        if (data?.saveTheoryPromptLayer?.success) {
          toast.success("Išsaugota");
          await loadLayers();
        } else {
          toast.error(data?.saveTheoryPromptLayer?.error || "Klaida");
        }
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : "Klaida");
      }
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await generateTheory({
        variables: { skillKey, subject, grade, model },
      });
      const result = data?.generateTheory;
      if (result?.success) {
        // Good prose that explains three of eight concepts looks finished in the
        // preview. Say which it is, or nobody finds out until a child presses
        // „Noriu suprasti" and the card has nothing to show them.
        const total = result.conceptsTotal ?? 0;
        const covered = result.conceptsCovered ?? 0;
        const missing: string[] = result.missingConcepts ?? [];
        if (total > 0 && missing.length > 0) {
          toast.warning(
            `Teorija sugeneruota, bet padengia tik ${covered}/${total} sąvokų. ` +
              `Trūksta: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`
          );
        } else {
          toast.success(
            total > 0 ? `Teorija sugeneruota — padengtos visos ${total} sąvokos` : "Teorija sugeneruota"
          );
        }
        setTheoryHtml(result.theoryHtml);
      } else {
        toast.error(result?.error || "Generavimo klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    } finally {
      setGenerating(false);
    }
  };

  const backUrl = `/admin/exercises?subject=${subject}&grade=${grade}`;

  if (!skillKey) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>Nenurodytas įgūdis.</p>
        <Link to="/admin/exercises" className="text-primary underline mt-2 inline-block">
          Grįžti į pratimų sąrašą
        </Link>
      </div>
    );
  }

  const layers = layersData?.layers || [];

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backUrl}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Atgal
          </Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Teorija
          </h1>
          <p className="text-sm text-muted-foreground">
            {skillName} <span className="text-xs">({skillKey})</span>
          </p>
        </div>
      </div>

      {/* Existing theory preview */}
      <Card>
        <CardContent className="pt-4 pb-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">
              Esama teorija
            </Label>
            {theoryHtml && (
              <Badge variant="outline" className="text-green-600 border-green-300">
                yra
              </Badge>
            )}
            {!theoryHtml && !theoryLoading && (
              <Badge variant="outline" className="text-muted-foreground">
                nėra
              </Badge>
            )}
          </div>
          {theoryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Kraunama...</span>
            </div>
          ) : theoryHtml ? (
            <div className="border rounded-md p-4 bg-muted/20 max-h-[400px] overflow-y-auto">
              <TheoryView html={theoryHtml} sourceOrder />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Šiam įgūdžiui teorija dar nesukurta.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Settings row */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>AI modelis</Label>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    Numatytasis (nemokamas &rarr; mokamas)
                  </SelectItem>
                  <SelectItem value="gemini-3.8-flash">
                    Flash 3.8
                  </SelectItem>
                  <SelectItem value="gemini-3.7-flash">
                    Flash 3.7
                  </SelectItem>
                  <SelectItem value="gemini-3.6-flash">
                    Flash 3.6
                  </SelectItem>
                  <SelectItem value="gemini-3.5-flash">
                    Flash 3.5
                  </SelectItem>
                  <SelectItem value="gemini-3.5-flash-lite">
                    Flash Lite 3.5
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prompt layers */}
      {layersLoading ? (
        <div className="flex items-center justify-center py-12 border rounded-md">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            Kraunami prompt sluoksniai...
          </span>
        </div>
      ) : showComposed ? (
        <Card>
          <CardContent className="pt-4 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">
                Sujungtas prompt
              </Label>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowComposed(false)}
              >
                <EyeOff className="h-3.5 w-3.5 mr-1" />
                Slėpti
              </Button>
            </div>
            <ComposedPromptView segments={layersData?.composed_segments || []} />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {layers.map((layer) => (
            <LayerAccordion
              key={layer.key}
              layer={layer}
              expanded={expandedLayers.has(layer.key)}
              onToggle={() => toggleLayer(layer.key)}
              content={editedLayers[layer.key] ?? layer.content ?? ""}
              onContentChange={(val) => setLayerContent(layer.key, val)}
              modified={isLayerModified(layer)}
              onSave={() => handleSaveLayer(layer)}
              saving={savingSkill || savingLayer}
            />
          ))}

          {layers.some((l) => l.exists) && (
            <div className="pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowComposed(true)}
              >
                <Eye className="h-3.5 w-3.5 mr-2" />
                Rodyti sujungtą prompt
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <Button
          onClick={handleGenerate}
          disabled={generating || layersLoading}
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Generuojama...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              {theoryHtml ? "Pergeneruoti" : "Generuoti teoriją"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (same pattern as ExerciseGeneratePage)
// ---------------------------------------------------------------------------

function LayerAccordion({
  layer,
  expanded,
  onToggle,
  content,
  onContentChange,
  modified,
  onSave,
  saving,
}: {
  layer: LayerInfo;
  expanded: boolean;
  onToggle: () => void;
  content: string;
  onContentChange: (val: string) => void;
  modified: boolean;
  onSave: () => void;
  saving: boolean;
}) {
  const isEmpty = !layer.exists && !content;

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent/50 transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        {layer.exists ? (
          <FileText className="h-3.5 w-3.5 shrink-0 text-primary" />
        ) : (
          <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className={`font-medium truncate ${!layer.exists ? "text-muted-foreground" : ""}`}>
          {layer.label}
        </span>
        <span className="text-xs text-muted-foreground truncate ml-1">
          {layer.filename}.md
        </span>
        <div className="ml-auto flex items-center gap-1.5 shrink-0">
          {modified && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-orange-600 border-orange-300">
              pakeistas
            </Badge>
          )}
          {!layer.exists && !modified && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
              tuščias
            </Badge>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t px-3 py-3 space-y-2 bg-muted/20">
          <Textarea
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            rows={isEmpty ? 4 : 12}
            className="font-mono text-xs"
            placeholder="Įveskite prompt fragmentą..."
          />
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Placeholder: {"{{SUBJECT_NAME}}"}, {"{{GRADE}}"}, {"{{AGE}}"}, {"{{SKILL_NAME}}"}
            </p>
            {modified && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onSave();
                }}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1" />
                )}
                Išsaugoti
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const SEGMENT_STYLES: Record<string, { border: string; bg: string; badge: string; badgeBg: string }> = {
  layer: {
    border: "border-l-blue-400",
    bg: "bg-blue-50/40 dark:bg-blue-950/20",
    badge: "text-blue-700 dark:text-blue-300",
    badgeBg: "bg-blue-100 dark:bg-blue-900/50",
  },
  shape_pack: {
    border: "border-l-emerald-400",
    bg: "bg-emerald-50/40 dark:bg-emerald-950/20",
    badge: "text-emerald-700 dark:text-emerald-300",
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/50",
  },
  coverage: {
    border: "border-l-amber-400",
    bg: "bg-amber-50/40 dark:bg-amber-950/20",
    badge: "text-amber-700 dark:text-amber-300",
    badgeBg: "bg-amber-100 dark:bg-amber-900/50",
  },
  concepts: {
    border: "border-l-sky-400",
    bg: "bg-sky-50/40 dark:bg-sky-950/20",
    badge: "text-sky-700 dark:text-sky-300",
    badgeBg: "bg-sky-100 dark:bg-sky-900/50",
  },
  skill: {
    border: "border-l-violet-400",
    bg: "bg-violet-50/40 dark:bg-violet-950/20",
    badge: "text-violet-700 dark:text-violet-300",
    badgeBg: "bg-violet-100 dark:bg-violet-900/50",
  },
};

const SEGMENT_KINDS: Record<string, string> = {
  layer: "sluoksnis",
  shape_pack: "SVG",
  coverage: "užduotys",
  concepts: "sąvokos",
  skill: "įgūdis",
};

function HighlightedContent({ text }: { text: string }) {
  const parts = text.split(/(«[^»]*»)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("«") && part.endsWith("»")) {
          const value = part.slice(1, -1);
          return (
            <span
              key={i}
              className="bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 rounded px-0.5"
            >
              {value}
            </span>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function ComposedPromptView({ segments }: { segments: ComposedSegment[] }) {
  if (segments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        (tuščia – nėra aktyvių sluoksnių)
      </p>
    );
  }

  return (
    <div className="space-y-2 max-h-[70vh] overflow-y-auto">
      {segments.map((seg, i) => {
        const style = SEGMENT_STYLES[seg.type] || SEGMENT_STYLES.layer;
        return (
          <div
            key={i}
            className={`border-l-4 rounded-r-md ${style.border} ${style.bg}`}
          >
            <div
              className={`select-none px-3 py-1.5 text-[11px] font-medium tracking-wide ${style.badge} ${style.badgeBg} rounded-tr-md flex items-center gap-2`}
            >
              <span className="opacity-60 uppercase">
                {seg.type === "layer" ? "sluoksnis" : seg.type === "shape_pack" ? "SVG" : "įgūdis"}
              </span>
              <span>{seg.label}</span>
            </div>
            <pre className="px-3 py-2 text-xs font-mono whitespace-pre-wrap break-words leading-relaxed">
              <HighlightedContent text={seg.content} />
            </pre>
          </div>
        );
      })}
    </div>
  );
}
